import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import styles from './controls.module.css';

const join = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
}

export function Select({ label, hint, id, className, children, ...rest }: SelectProps) {
  const auto = useId();
  const sid = id ?? auto;
  return (
    <div className={join(styles.field, className)}>
      <label htmlFor={sid} className={styles.label}>
        {label}
      </label>
      <select id={sid} className={styles.select} {...rest}>
        {children}
      </select>
      {hint ? <p className={styles.hint}>{hint}</p> : null}
    </div>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
}

export function TextArea({ label, hint, id, className, ...rest }: TextAreaProps) {
  const auto = useId();
  const tid = id ?? auto;
  return (
    <div className={join(styles.field, className)}>
      <label htmlFor={tid} className={styles.label}>
        {label}
      </label>
      <textarea id={tid} className={styles.textarea} {...rest} />
      {hint ? <p className={styles.hint}>{hint}</p> : null}
    </div>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  hint?: string;
}

export function Checkbox({ label, hint, id, className, ...rest }: CheckboxProps) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <div className={join(styles.check, className)}>
      <input id={cid} type="checkbox" className={styles.box} {...rest} />
      <label htmlFor={cid} className={styles.checkLabel}>
        {label}
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </label>
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'error' | 'success'; children: ReactNode }) {
  return (
    <div className={join(styles.notice, styles[tone])} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className={styles.pageHeader}>
      <h1 className={styles.pageTitle}>{title}</h1>
      {children ? <div className={styles.pageActions}>{children}</div> : null}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className={styles.empty}>{children}</p>;
}

/** Tag für kurze Zustände („offline“, „Admin“). Immer mit Text, Farbe ist nur Verstärkung. */
export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' | 'busy' }) {
  return <span className={join(styles.tag, styles[`tag_${tone}`])}>{children}</span>;
}
